<template>
  <!-- Begin page -->
  <div id="layout-wrapper">

    <NavigationBar/>
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">

          <!-- start page title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Departmental HR Monthly Report</h4>

                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="index.html">Home</a></li>
                    <li class="breadcrumb-item active">Reports</li>
                    <li class="breadcrumb-item active">Departmental HR Monthly Report</li>
                  </ol>
                </div>

              </div>
            </div>
          </div>
          <!-- end page title -->

          <!-- Month selection and loading indicator -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-center">
                    <div class="input-group fw-semibold text-uppercase me-3" style="width: 250px;">
                      <label class="input-group-text text-uppercase fw-bold bg-primary text-white">Select Month</label>
                      <select v-model="selectedMonth" class="form-select" @change="getHRMonthlyReport">
                        <option value="">Select Month</option>
                        <option v-for="(month, index) in months" :key="index" :value="month.value">{{ month.name }}</option>
                      </select>
                    </div>

                    <!-- Loading spinner -->
                    <div v-if="loading" class="d-flex align-items-center">
                      <div class="spinner-border spinner-border-sm text-primary me-2" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                      <span class="text-muted">Loading data...</span>
                    </div>

                    <!-- Report date -->
                    <div v-else class="text-muted">
                      Last updated: {{ lastUpdated }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- stats cards -->
          <div class="row" v-if="!loading">
            <!-- Wage Bill Change -->
            <div class="col-lg-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">Wage Bill</p>
                      <h5 class="mb-0">{{ summary.wageBillPercentage }}%</h5>
                      <p class="text-muted small">Last Month: {{ summary.previousWageBillPercentage }}%</p>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title bg-primary rounded-circle text-white font-size-20">
                        <i class="mdi mdi-cash-multiple"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- New Hires -->
            <div class="col-lg-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">New Hires</p>
                      <h5 class="mb-0">{{ summary.totalNewHires }}</h5>
                      <p class="text-muted small">Last Month: {{ summary.previousTotalNewHires }}</p>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title bg-success rounded-circle text-white font-size-20">
                        <i class="mdi mdi-account-plus"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Staff Exits -->
            <div class="col-lg-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">Staff Exits</p>
                      <h5 class="mb-0">{{ summary.totalExits }}</h5>
                      <p class="text-muted small">Last Month: {{ summary.previousTotalExits }}</p>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title bg-danger rounded-circle text-white font-size-20">
                        <i class="mdi mdi-account-remove"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Total Workforce -->
            <div class="col-lg-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">Total Workforce</p>
                      <h5 class="mb-0">{{ summary.totalWorkforce }}</h5>
                      <p class="text-muted small">Fixed Workforce</p>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title bg-info rounded-circle text-white font-size-20">
                        <i class="mdi mdi-account-group"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- end of stats -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-white pt-0 pr-0 p-0 d-flex justify-content-between align-items-center w-100 border-bottom">
                  <div class="btn-toolbar p-3 d-flex justify-content-between align-items-center w-100" role="toolbar">
                    <div class="flex-grow-1">
                      <div class="search-box mb-0 me-3">
                        <div class="position-relative">
                          <input type="text" v-model="searchQuery" class="form-control bg-light border-light rounded" placeholder="Search department..." @input="filterDepartments">
                          <i class="bx bx-search-alt search-icon"></i>
                        </div>
                      </div>
                    </div>

                    <div class="d-flex">
                      <div class="d-flex align-items-center me-3 pe-3">
                        <div>
                          <div class="float-end d-flex ">
                            <div class="input-group fw-semibold text-uppercase me-3">
                              <label class="input-group-text text-uppercase">Department</label>
                              <select v-model="selectedDepartment" class="form-select" style="width: 220px;" @change="filterByDepartment">
                                <option value="">All Departments</option>
                                <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                                  {{ dept.departmentName }}
                                </option>
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Download Report Button -->
                      <button @click="downloadReport" type="button" class="btn btn-primary dropdown-toggle option-selector me-3 text-capitalize" :disabled="loading">
                        <i class="bx bxs-download"></i> <span class="pl-1 d-md-inline">Download Report</span>
                      </button>

                    </div>
                  </div>
                </div>

                <div class="card-body">
                  <!-- Loading state -->
                  <div v-if="loading" class="text-center p-5">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                    <p class="mt-3 text-muted">Loading departmental data...</p>
                    <div class="progress mt-2" style="height: 6px;">
                      <div class="progress-bar progress-bar-striped progress-bar-animated" role="progressbar"
                           style="width: 100%;" aria-valuenow="100" aria-valuemin="0" aria-valuemax="100"></div>
                    </div>
                  </div>

                  <!-- Empty state when no data or filter returns empty -->
                  <div v-else-if="filteredDepartmentData.length === 0" class="text-center py-5">
                    <i class="mdi mdi-database-search font-size-48 text-muted"></i>
                    <h5 class="mt-3">No departments found</h5>
                    <p class="text-muted">Try adjusting your search criteria</p>
                    <button @click="resetFilters" class="btn btn-sm btn-outline-primary mt-2">Reset Filters</button>
                  </div>

                  <!-- Actual data table -->
                  <div v-else class="table-responsive">
                    <table class="table align-middle table-hover table-striped text-nowrap text-uppercase">
                      <thead class="table-light">
                      <tr class="table-dark">
                        <th scope="col">Department</th>
                        <th class="" scope="col">Staff (Previous Month)</th>
                        <th class="" scope="col">Wage Bill (Previous Month)</th>
                        <th class="" scope="col">Staff (Current Month)</th>
                        <th class="" scope="col">Wage Bill (Current Month)</th>
                        <th scope="col">Exits</th>
                        <th scope="col">New Hires</th>
                        <th scope="col">Net Change</th>
                        <th scope="col">Wage Diff</th>
                        <th scope="col">Avg Salary</th>
                        <th scope="col">Action</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="dept in filteredDepartmentData" :key="dept.id">
                        <td>{{ dept.name }}</td>
                        <td class="table-primary">{{ dept.previousStaff }}</td>
                        <td class="table-primary">{{ formatCurrency(dept.previousWageBill) }}</td>
                        <td class="table-secondary">{{ dept.currentStaff }}</td>
                        <td class="table-secondary">{{ formatCurrency(dept.currentWageBill) }}</td>
                        <td>{{ dept.exits }}</td>
                        <td>{{ dept.newHires }}</td>
                        <td :class="getNetChangeClass(dept.netChange)">{{ dept.netChange > 0 ? '+' + dept.netChange : dept.netChange }}</td>
                        <td :class="getWageDiffClass(dept.wageDiff)">{{ formatCurrency(dept.wageDiff, true) }}</td>
                        <td>{{ formatCurrency(dept.avgSalary) }}</td>
                        <td><button class="btn btn-sm btn-primary" @click="viewDepartmentDetails(dept.id)">View</button></td>
                      </tr>
                      </tbody>
                      <tfoot class="table-dark">
                      <tr class="fw-bold">
                        <td>Total Workforce</td>
                        <td class="table-primary">{{ summary.totalPreviousStaff }}</td>
                        <td class="table-primary">{{ formatCurrency(summary.totalPreviousWageBill) }}</td>
                        <td class="table-secondary">{{ summary.totalCurrentStaff }}</td>
                        <td class="table-secondary">{{ formatCurrency(summary.totalCurrentWageBill) }}</td>
                        <td>{{ summary.totalExits }}</td>
                        <td>{{ summary.totalNewHires }}</td>
                        <td :class="getNetChangeClass(summary.totalNetChange)">{{ summary.totalNetChange > 0 ? '+' + summary.totalNetChange : summary.totalNetChange }}</td>
                        <td :class="getWageDiffClass(summary.totalWageDiff)">{{ formatCurrency(summary.totalWageDiff, true) }}</td>
                        <td>{{ formatCurrency(summary.avgSalary) }}</td>
                        <td></td>
                      </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <!-- end col -->
          </div>
          <!-- end row -->
        </div>
        <!-- container-fluid -->
      </div>
    </div>
    <!-- end main content-->
  </div>
  <!-- END layout-wrapper -->

  <!-- Department Details Modal -->
  <div class="modal fade" id="departmentDetailsModal" tabindex="-1" aria-labelledby="departmentDetailsModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="departmentDetailsModalLabel">Department Details</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="loading" class="text-center p-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading department details...</p>
          </div>
          <div v-else-if="selectedDeptDetails">
            <h4 class="mb-3">{{ selectedDeptDetails.name }}</h4>
            <div class="row g-3 mb-4">
              <div class="col-md-6">
                <div class="card border-light">
                  <div class="card-header bg-light">Current Month Statistics</div>
                  <div class="card-body">
                    <ul class="list-group list-group-flush">
                      <li class="list-group-item d-flex justify-content-between align-items-center">
                        Total Staff
                        <span class="badge bg-primary rounded-pill">{{ selectedDeptDetails.currentStaff }}</span>
                      </li>
                      <li class="list-group-item d-flex justify-content-between align-items-center">
                        Wage Bill
                        <span class="badge bg-primary rounded-pill">{{ formatCurrency(selectedDeptDetails.currentWageBill) }}</span>
                      </li>
                      <li class="list-group-item d-flex justify-content-between align-items-center">
                        Average Salary
                        <span class="badge bg-info rounded-pill">{{ formatCurrency(selectedDeptDetails.avgSalary) }}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div class="card border-light">
                  <div class="card-header bg-light">Staff Movement</div>
                  <div class="card-body">
                    <ul class="list-group list-group-flush">
                      <li class="list-group-item d-flex justify-content-between align-items-center">
                        New Hires
                        <span class="badge bg-success rounded-pill">{{ selectedDeptDetails.newHires }}</span>
                      </li>
                      <li class="list-group-item d-flex justify-content-between align-items-center">
                        Exits
                        <span class="badge bg-danger rounded-pill">{{ selectedDeptDetails.exits }}</span>
                      </li>
                      <li class="list-group-item d-flex justify-content-between align-items-center">
                        Net Change
                        <span class="badge" :class="getNetChangeBadgeClass(selectedDeptDetails.netChange)">
                          {{ selectedDeptDetails.netChange > 0 ? '+' + selectedDeptDetails.netChange : selectedDeptDetails.netChange }}
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <!-- Additional department metrics could go here -->
            <div class="card mb-3">
              <div class="card-header bg-light">Staff Distribution</div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6">
                    <h6 class="text-muted mb-3">Staff by Gender</h6>
                    <div class="progress mb-4" style="height: 25px;">
                      <div class="progress-bar bg-primary" role="progressbar"
                           :style="{width: selectedDeptDetails.genderDistribution?.male + '%'}"
                           aria-valuenow="selectedDeptDetails.genderDistribution?.male"
                           aria-valuemin="0" aria-valuemax="100">
                        Male: {{ selectedDeptDetails.genderDistribution?.male }}%
                      </div>
                      <div class="progress-bar bg-info" role="progressbar"
                           :style="{width: selectedDeptDetails.genderDistribution?.female + '%'}"
                           aria-valuenow="selectedDeptDetails.genderDistribution?.female"
                           aria-valuemin="0" aria-valuemax="100">
                        Female: {{ selectedDeptDetails.genderDistribution?.female }}%
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <h6 class="text-muted mb-3">Staff by Contract Type</h6>
                    <div class="progress mb-2" style="height: 20px;">
                      <div class="progress-bar bg-success" role="progressbar"
                           :style="{width: selectedDeptDetails.contractDistribution?.permanent + '%'}"
                           aria-valuenow="selectedDeptDetails.contractDistribution?.permanent"
                           aria-valuemin="0" aria-valuemax="100">
                        Permanent: {{ selectedDeptDetails.contractDistribution?.permanent }}%
                      </div>
                    </div>
                    <div class="progress mb-2" style="height: 20px;">
                      <div class="progress-bar bg-warning" role="progressbar"
                           :style="{width: selectedDeptDetails.contractDistribution?.contract + '%'}"
                           aria-valuenow="selectedDeptDetails.contractDistribution?.contract"
                           aria-valuemin="0" aria-valuemax="100">
                        Contract: {{ selectedDeptDetails.contractDistribution?.contract }}%
                      </div>
                    </div>
                    <div class="progress" style="height: 20px;">
                      <div class="progress-bar bg-secondary" role="progressbar"
                           :style="{width: selectedDeptDetails.contractDistribution?.temporary + '%'}"
                           aria-valuenow="selectedDeptDetails.contractDistribution?.temporary"
                           aria-valuemin="0" aria-valuemax="100">
                        Temporary: {{ selectedDeptDetails.contractDistribution?.temporary }}%
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="alert alert-warning">
            No department details available
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          <button type="button" class="btn btn-primary" @click="generateDepartmentReport" :disabled="!selectedDeptDetails">
            <i class="mdi mdi-file-pdf-outline me-1"></i>Export Report
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import {paysol, execute, authUrl, biller, executeDownload} from "@/api";
import moment from "moment/moment";
import * as XLSX from 'xlsx';

export default {
  name: "ReportHhrReport",
  components: {NavigationBar},
  data() {
    return {
      loading: false,
      searchQuery: '',
      selectedMonth: this.getCurrentMonth(),
      selectedDepartment: '',
      departmentData: [],
      filteredDepartmentData: [],
      departments: [],
      months: [
        { name: 'January', value: '01' },
        { name: 'February', value: '02' },
        { name: 'March', value: '03' },
        { name: 'April', value: '04' },
        { name: 'May', value: '05' },
        { name: 'June', value: '06' },
        { name: 'July', value: '07' },
        { name: 'August', value: '08' },
        { name: 'September', value: '09' },
        { name: 'October', value: '10' },
        { name: 'November', value: '11' },
        { name: 'December', value: '12' }
      ],
      summary: {
        totalWorkforce: 0,
        totalPreviousStaff: 0,
        totalCurrentStaff: 0,
        totalPreviousWageBill: 0,
        totalCurrentWageBill: 0,
        totalExits: 0,
        totalNewHires: 0,
        totalNetChange: 0,
        totalWageDiff: 0,
        avgSalary: 0,
        wageBillPercentage: 0,
        previousWageBillPercentage: 0,
        previousTotalNewHires: 0,
        previousTotalExits: 0
      },
      lastUpdated: '',
      departmentModal: null,
      selectedDeptDetails: null
    };
  },
  mounted() {
    this.getDepartments();
    this.getHRMonthlyReport();
  },

  methods: {
    getCurrentMonth() {
      const now = new Date();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      return month;
    },

    getDepartments() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getDepartments");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.departments = res.data.data.departments || [];
            } else {
              console.error("Error fetching departments:", res.data.message || "Unknown error");
              // Display error to user
              this.showToast("error", "Failed to load departments: " + (res.data.message || "Unknown error"));
            }
            this.loading = false;
          })
          .catch((error) => {
            console.error("API error when fetching departments:", error);
            this.showToast("error", "Network error: " + (error.message || "Unknown error"));
            this.loading = false;
          });
    },

    getHRMonthlyReport() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getHRMonthlyReport");
      data.append("month", this.selectedMonth);

      if (this.selectedDepartment) {
        data.append("departmentId", this.selectedDepartment);
      }

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              // Update data with API response
              this.departmentData = res.data.data.departments || [];
              this.filteredDepartmentData = [...this.departmentData];
              this.summary = res.data.data.summary || this.summary;
              this.lastUpdated = res.data.data.lastUpdated || new Date().toLocaleString();

              // Apply any active filters
              if (this.searchQuery) {
                this.filterDepartments();
              }

              this.showToast("success", "HR report data updated successfully");
            } else {
              console.error("Error fetching HR report data:", res.data.message || "Unknown error");
              this.showToast("error", "Failed to load HR report: " + (res.data.message || "Unknown error"));
            }
            this.loading = false;
          })
          .catch((error) => {
            console.error("API error when fetching HR report:", error);
            this.showToast("error", "Network error: " + (error.message || "Unknown error"));
            this.loading = false;
          });
    },

    filterDepartments() {
      if (!this.searchQuery) {
        this.filteredDepartmentData = [...this.departmentData];
        return;
      }

      const query = this.searchQuery.toLowerCase();
      this.filteredDepartmentData = this.departmentData.filter(dept =>
          dept.name.toLowerCase().includes(query)
      );
    },

    filterByDepartment() {
      this.getHRMonthlyReport(); // Re-fetch with department filter
    },

    resetFilters() {
      this.searchQuery = '';
      this.selectedDepartment = '';
      this.filterDepartments();
      this.getHRMonthlyReport();
    },

    formatCurrency(amount, showSign = false) {
      if (amount === undefined || amount === null) return 'N/A';

      const formatter = new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        maximumFractionDigits: 0
      });

      let formattedAmount = formatter.format(amount);

      if (showSign && amount > 0) {
        formattedAmount = '+' + formattedAmount;
      }

      return formattedAmount;
    },

    getNetChangeClass(value) {
      if (value > 0) return 'text-success fw-bold';
      if (value < 0) return 'text-danger fw-bold';
      return '';
    },

    getNetChangeBadgeClass(value) {
      if (value > 0) return 'bg-success rounded-pill';
      if (value < 0) return 'bg-danger rounded-pill';
      return 'bg-secondary rounded-pill';
    },

    getWageDiffClass(value) {
      if (value > 0) return 'text-success fw-bold';
      if (value < 0) return 'text-danger fw-bold';
      return '';
    },

    viewDepartmentDetails(deptId) {
      this.loading = true;
      // Find department basic info
      const dept = this.departmentData.find(d => d.id === deptId);

      if (!dept) {
        this.showToast("error", "Department not found");
        this.loading = false;
        return;
      }

      // Fetch detailed department info
      const data = new FormData();
      data.append("function", "getDepartmentDetails");
      data.append("departmentId", deptId);
      data.append("month", this.selectedMonth);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              // Combine basic info with detailed info
              this.selectedDeptDetails = {
                ...dept,
                ...res.data.data.departmentDetails,
                // Add mock data for distribution charts (if not provided by API)
                genderDistribution: res.data.data.departmentDetails?.genderDistribution || {
                  male: 60,
                  female: 40
                },
                contractDistribution: res.data.data.departmentDetails?.contractDistribution || {
                  permanent: 70,
                  contract: 20,
                  temporary: 10
                }
              };

              // Show modal
              const modal = new bootstrap.Modal(document.getElementById('departmentDetailsModal'));
              modal.show();
            } else {
              console.error("Error fetching department details:", res.data.message || "Unknown error");
              this.showToast("error", "Failed to load department details: " + (res.data.message || "Unknown error"));
            }
            this.loading = false;
          })
          .catch((error) => {
            console.error("API error when fetching department details:", error);
            this.showToast("error", "Network error: " + (error.message || "Unknown error"));
            this.loading = false;
          });
    },

    downloadReport() {
      this.loading = true;

      try {
        // Create a new workbook
        const workbook = XLSX.utils.book_new();

        // Create a worksheet for summary data
        const summaryData = [
          ['HR Monthly Report', '', '', ''],
          ['Month:', this.getMonthName(this.selectedMonth), '', ''],
          ['Department:', this.selectedDepartment ? this.getDepartmentName(this.selectedDepartment) : 'All Departments', '', ''],
          ['Generated On:', new Date().toLocaleString(), '', ''],
          ['', '', '', ''],
          ['Key Metrics', 'Current', 'Previous', 'Change'],
          ['Total Workforce', this.summary.totalCurrentStaff, this.summary.totalPreviousStaff, this.summary.totalNetChange],
          ['Wage Bill', this.formatCurrencyForExcel(this.summary.totalCurrentWageBill), this.formatCurrencyForExcel(this.summary.totalPreviousWageBill), this.formatCurrencyForExcel(this.summary.totalWageDiff)],
          ['New Hires', this.summary.totalNewHires, this.summary.previousTotalNewHires, this.summary.totalNewHires - this.summary.previousTotalNewHires],
          ['Staff Exits', this.summary.totalExits, this.summary.previousTotalExits, this.summary.totalExits - this.summary.previousTotalExits],
          ['Average Salary', this.formatCurrencyForExcel(this.summary.avgSalary), '', '']
        ];

        // Add summary worksheet
        const summaryWorksheet = XLSX.utils.aoa_to_sheet(summaryData);
        XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Summary');

        // Create headers for detailed department data
        const headers = [
          'Department',
          'Staff (Previous)',
          'Wage Bill (Previous)',
          'Staff (Current)',
          'Wage Bill (Current)',
          'Exits',
          'New Hires',
          'Net Change',
          'Wage Diff',
          'Avg Salary'
        ];

        // Create data rows
        const departmentDataRows = this.filteredDepartmentData.map(dept => [
          dept.name,
          dept.previousStaff,
          this.formatCurrencyForExcel(dept.previousWageBill),
          dept.currentStaff,
          this.formatCurrencyForExcel(dept.currentWageBill),
          dept.exits,
          dept.newHires,
          dept.netChange,
          this.formatCurrencyForExcel(dept.wageDiff),
          this.formatCurrencyForExcel(dept.avgSalary)
        ]);

        // Add total row
        departmentDataRows.push([
          'Total Workforce',
          this.summary.totalPreviousStaff,
          this.formatCurrencyForExcel(this.summary.totalPreviousWageBill),
          this.summary.totalCurrentStaff,
          this.formatCurrencyForExcel(this.summary.totalCurrentWageBill),
          this.summary.totalExits,
          this.summary.totalNewHires,
          this.summary.totalNetChange,
          this.formatCurrencyForExcel(this.summary.totalWageDiff),
          this.formatCurrencyForExcel(this.summary.avgSalary)
        ]);

        // Create department data worksheet
        const deptData = [headers, ...departmentDataRows];
        const deptWorksheet = XLSX.utils.aoa_to_sheet(deptData);
        XLSX.utils.book_append_sheet(workbook, deptWorksheet, 'Department Data');

        // Set column widths for better readability
        const summaryColumnWidths = [{ wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 15 }];
        summaryWorksheet['!cols'] = summaryColumnWidths;

        const deptColumnWidths = [
          { wch: 40 }, // Department name
          { wch: 15 }, // Previous Staff
          { wch: 20 }, // Previous Wage Bill
          { wch: 15 }, // Current Staff
          { wch: 20 }, // Current Wage Bill
          { wch: 10 }, // Exits
          { wch: 10 }, // New Hires
          { wch: 10 }, // Net Change
          { wch: 15 }, // Wage Diff
          { wch: 15 }  // Avg Salary
        ];
        deptWorksheet['!cols'] = deptColumnWidths;

        // Generate filename with month and year
        const monthName = this.getMonthName(this.selectedMonth);
        const year = new Date().getFullYear();
        const filename = `HR_Monthly_Report_${monthName}_${year}.xlsx`;

        // Write the Excel file
        XLSX.writeFile(workbook, filename);

        this.showToast("success", "Report downloaded successfully");
      } catch (error) {
        console.error("Error generating Excel file:", error);
        this.showToast("error", "Failed to generate report: " + error.message);
      } finally {
        this.loading = false;
      }
    },

    formatCurrencyForExcel(value) {
      if (value === undefined || value === null) return '';
      return value; // Just return the numeric value for Excel
    },

    getMonthName(monthNumber) {
      const month = this.months.find(m => m.value === monthNumber);
      return month ? month.name : '';
    },

    getDepartmentName(departmentId) {
      const department = this.departments.find(d => d.id === departmentId);
      return department ? department.name : '';
    },

    generateDepartmentReport() {
      if (!this.selectedDeptDetails) return;

      this.loading = true;

      try {
        // Create a new workbook
        const workbook = XLSX.utils.book_new();

        // Department header
        const deptHeader = [
          ['Department HR Report: ' + this.selectedDeptDetails.name, '', ''],
          ['Month:', this.getMonthName(this.selectedMonth), ''],
          ['Generated On:', new Date().toLocaleString(), ''],
          ['', '', '']
        ];

        // Department summary
        const deptSummary = [
          ['Metric', 'Current Month', 'Previous Month'],
          ['Total Staff', this.selectedDeptDetails.currentStaff, this.selectedDeptDetails.previousStaff],
          ['Wage Bill', this.formatCurrencyForExcel(this.selectedDeptDetails.currentWageBill), this.formatCurrencyForExcel(this.selectedDeptDetails.previousWageBill)],
          ['New Hires', this.selectedDeptDetails.newHires, ''],
          ['Exits', this.selectedDeptDetails.exits, ''],
          ['Net Change', this.selectedDeptDetails.netChange, ''],
          ['Average Salary', this.formatCurrencyForExcel(this.selectedDeptDetails.avgSalary), ''],
          ['', '', '']
        ];

        // Gender distribution
        const genderData = [
          ['Gender Distribution', '', ''],
          ['Male', this.selectedDeptDetails.genderDistribution?.male + '%', ''],
          ['Female', this.selectedDeptDetails.genderDistribution?.female + '%', ''],
          ['', '', '']
        ];

        // Contract distribution
        const contractData = [
          ['Contract Type Distribution', '', ''],
          ['Permanent', this.selectedDeptDetails.contractDistribution?.permanent + '%', ''],
          ['Contract', this.selectedDeptDetails.contractDistribution?.contract + '%', ''],
          ['Temporary', this.selectedDeptDetails.contractDistribution?.temporary + '%', '']
        ];

        // Combine all data into one array
        const allData = [...deptHeader, ...deptSummary, ...genderData, ...contractData];

        // Create worksheet
        const worksheet = XLSX.utils.aoa_to_sheet(allData);

        // Set column widths
        worksheet['!cols'] = [{ wch: 25 }, { wch: 20 }, { wch: 20 }];

        // FIX: Truncate sheet name to 31 characters maximum (Excel limitation)
        let sheetName = this.selectedDeptDetails.name;
        if (sheetName.length > 31) {
          sheetName = sheetName.substring(0, 28) + '...';
        }

        // Add to workbook with truncated sheet name
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

        // Generate filename (filename can be longer than sheet name)
        const filename = `Dept_HR_Report_${this.getMonthName(this.selectedMonth)}.xlsx`;

        // Write the file
        XLSX.writeFile(workbook, filename);

        this.showToast("success", "Department report downloaded successfully");
      } catch (error) {
        console.error("Error generating department report:", error);
        this.showToast("error", "Failed to generate department report: " + error.message);
      } finally {
        this.loading = false;
      }
    },
    showToast(type, message) {
      // Check if toast plugin exists before using it
      if (this.$toast) {
        this.$toast[type](message);
      } else {
        console.log(`Toast (${type}): ${message}`);
        // Fallback alert for critical errors if no toast component
        if (type === 'error') {
          alert(message);
        }
      }
    }
  },

  watch: {
    searchQuery: {
      handler(value) {
        this.filterDepartments();
      },
      // Add debounce for better performance
      immediate: false
    }
  }
};
</script>

<style scoped>
/* Card styling */
.card {
  border-radius: 0.5rem;
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  transition: all 0.3s ease;
  margin-bottom: 1.5rem;
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

/* Font sizing */
.font-size-20 {
  font-size: 1.25rem !important;
}

.font-size-48 {
  font-size: 3rem !important;
}

/* Avatar styling */
.avatar-sm {
  width: 2.5rem;
  height: 2.5rem;
}

.avatar-title {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-weight: 500;
}

/* Table styling */
.table td, .table th {
  padding: 0.75rem;
  vertical-align: middle;
}

.table-striped tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.02);
}

.table-hover tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

/* Spinner and loading styles */
.spinner-border, .spinner-grow {
  width: 3rem;
  height: 3rem;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

/* Progress bar styling */
.progress {
  background-color: #eef0fc;
  border-radius: 1rem;
  height: 6px;
}

.progress-bar-striped {
  background-image: linear-gradient(45deg, rgba(255, 255, 255, 0.15) 25%, transparent 25%, transparent 50%, rgba(255, 255, 255, 0.15) 50%, rgba(255, 255, 255, 0.15) 75%, transparent 75%, transparent);
  background-size: 1rem 1rem;
}

.progress-bar-animated {
  animation: progress-bar-stripes 1s linear infinite;
}

@keyframes progress-bar-stripes {
  from { background-position: 1rem 0 }
  to { background-position: 0 0 }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .card-body {
    padding: 1rem;
  }

  .table-responsive {
    -webkit-overflow-scrolling: touch;
  }

  .d-md-inline {
    display: none;
  }

  .input-group, .btn-group {
    width: 100%;
    margin-bottom: 1rem;
  }
}
</style>